// Guided Walkthrough definitions.
// Each step: { route?, expandMenu?, selector?, title, body }
// - route: navigate here before showing the step
// - expandMenu: staff sidebar group testid suffix to open (e.g. 'loan-management')
// - selector: element to spotlight (falls back to a centered card if not found)

const STAFF_TOURS = [
  {
    id: 'staff-add-customer',
    title: 'Add a customer',
    description: 'Register a new customer from start to finish',
    audience: 'staff',
    steps: [
      {
        selector: '[data-testid="nav-customers"]',
        title: 'Open Customers',
        body: "Everyone you register lives in the Customers section. Open it from the sidebar to get started.",
      },
      {
        route: '/staff/customers',
        selector: '[data-testid="add-customer-btn"]',
        title: 'Add a new customer',
        body: "Click \u201cAdd Customer\u201d to open the registration form.",
      },
      {
        route: '/staff/customers',
        selector: '[data-testid="add-customer-btn"]',
        title: 'Fill in the details',
        body: "Enter the customer\u2019s personal, work and bank details, then save. The new customer appears in the list right away and can be linked to a loan.",
      },
    ],
  },
  {
    id: 'staff-approve-loan',
    title: 'Approve & disburse a loan',
    description: 'Review an application, approve it, then pay it out',
    audience: 'staff',
    steps: [
      {
        expandMenu: 'loan-management',
        selector: '[data-testid="nav-loan-management"]',
        title: 'Open Loan Management',
        body: "Every loan task lives under Loan Management. Open it from the sidebar.",
      },
      {
        expandMenu: 'loan-management',
        route: '/staff/loans-hub',
        selector: '[data-testid="nav-loans-hub"]',
        title: 'Go to Loans Hub',
        body: "Loans Hub lists all applications waiting for a decision.",
      },
      {
        route: '/staff/loans-hub',
        selector: '[data-testid="loans-hub-search-input"]',
        title: 'Find the application',
        body: "Search by name, phone, BBC ID or IPPIS to open the right application.",
      },
      {
        route: '/staff/loans-hub',
        selector: '[data-testid="loans-hub-page"]',
        title: 'Approve, then disburse',
        body: "Open a pending application, review the amount and schedule, then click Approve. Approved loans move to disbursement where you confirm the payout \u2014 the loan then becomes active.",
      },
    ],
  },
];

const CUSTOMER_TOURS = [
  {
    id: 'customer-apply-loan',
    title: 'Apply for a loan',
    description: 'Submit a new loan application',
    audience: 'customer',
    steps: [
      {
        selector: '[data-testid="customer-nav-apply-for-loan"]',
        title: 'Start your application',
        body: "Tap \u201cApply for Loan\u201d in the menu to begin a new application.",
      },
      {
        route: '/customer/apply',
        selector: '[data-testid="loan-application"]',
        title: 'Choose your loan',
        body: "Pick a loan product, enter the amount and term, then follow the steps. You\u2019ll see a repayment preview before you submit.",
      },
    ],
  },
  {
    id: 'customer-request-topup',
    title: 'Request a Top-Up',
    description: 'Borrow more on top of your current loan',
    audience: 'customer',
    steps: [
      {
        selector: '[data-testid="customer-nav-my-loans"]',
        title: 'Open My Loans',
        body: "Go to \u201cMy Loans\u201d to manage your active loans and top-ups.",
      },
      {
        route: '/customer/loans',
        selector: '[data-testid="apply-new-loan-btn"]',
        title: 'Start a Top-Up',
        body: "Tap \u201cApply for New Loan\u201d and choose the Top-Up / Future Advance option to borrow more while your current loan is still running.",
      },
      {
        route: '/customer/topup-calculator',
        selector: '[data-testid="loan-calculator"]',
        title: 'Check how much you qualify for',
        body: "Use the Top-Up Calculator to see the amount you\u2019re eligible for and your new repayment before you apply.",
      },
    ],
  },
];

export const getTours = (role) => {
  const isCustomer = ['customer', 'investor'].includes(role);
  return isCustomer ? CUSTOMER_TOURS : STAFF_TOURS;
};

// Keyword triggers so the assistant can offer the matching walkthrough
// when a user asks a "how do I..." question.
const TOUR_TRIGGERS = {
  'staff-add-customer': ['add customer', 'add a customer', 'register customer', 'register a customer', 'new customer', 'create customer', 'onboard'],
  'staff-approve-loan': ['approve loan', 'approve a loan', 'approve the loan', 'approve and disburse', 'disburse loan', 'disburse a loan', 'disbursement'],
  'customer-apply-loan': ['apply for a loan', 'apply for loan', 'apply loan', 'loan application', 'how do i apply', 'request a loan', 'get a loan'],
  'customer-request-topup': ['top-up', 'top up', 'topup', 'request a top', 'additional loan', 'borrow more', 'increase my loan', 'future advance'],
};

export const matchTour = (text, role) => {
  if (!text) return null;
  const t = text.toLowerCase();
  for (const tour of getTours(role)) {
    const kws = TOUR_TRIGGERS[tour.id] || [];
    if (kws.some((k) => t.includes(k))) return tour;
  }
  return null;
};

export const startTour = (tourId) => {
  window.dispatchEvent(new CustomEvent('bbc:start-tour', { detail: { tourId } }));
};
